import React, { Fragment } from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import Image from "../../Image";
import LinkComponent from "../../LinkComponent";
import Markdown from "../../Markdown";
import { SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";
import HeadlineItem from "./HeadlineItem";

const Text = styled(Markdown)`
    font-size: 1em;
    ${({ theme }) => css`
        color: ${theme.colors.blue.toString()};
    `};

    margin: 20px 0;
`;

const LinkWrapper = styled.div`
    display: block;
    margin: 20px 0;
`;

const Container = styled.div`
    > :first-child {
        &,
        &${Text} > :first-child {
            margin-top: 0;
        }
    }

    > :last-child {
        &,
        &${Text} > :last-child {
            margin-bottom: 0;
        }
    }
`;

const SectionHeadlineItem = styled(HeadlineItem)`
    hyphens: auto;
    word-break: break-word;
    overflow-wrap: break-word;
    scroll-margin-top: 80px;
`;

const ContentContainer = styled.div`
    ${({ theme }) => css`
        ${theme.paddings.containerOuter.right}
        ${theme.paddings.containerOuter.left}
    `}
`;

const ContentElement = ({ items }) => {
    const theme = useTheme();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);

    const columns = css`
        ${theme.breakpoints.from.small.css`
           column-count: 2;
           column-gap: 30px;
        `};
    `;

    const getBackgroundColor = (color) => {
        return css`
            background-color: ${theme.colors[color]
                ? theme.colors[color].toString()
                : theme.colors.white.toString()};
        `;
    };

    const getPaddingValue = (paddingType) => {
        switch (paddingType) {
            case "0_none":
                return "0px";
            case "1_small":
                return "20px";
            case "2_medium":
                return "40px";
            case "3_large":
                return "60px";
            default:
                return "0px";
        }
    };

    return (
        <Container>
            {items.map((item, index) => {
                return (
                    <Fragment key={index}>
                        <ContentContainer
                            css={css`
                                padding-top: ${getPaddingValue(
                                    item.paddingTop
                                )};
                                padding-bottom: ${getPaddingValue(
                                    item.paddingBottom
                                )};
                                ${getBackgroundColor(item.backgroundColor)}
                            `}
                        >
                            <SideNavContainer paddingLeft={paddingLeft}>
                                {item.name && (
                                    <SectionHeadlineItem
                                        id={item.id}
                                        headline={item.headlineNumber}
                                        color={item.headlineColor}
                                    >
                                        {item.name}
                                    </SectionHeadlineItem>
                                )}
                                {item.text && (
                                    <Text css={item.withColumns && columns}>
                                        {item.text}
                                    </Text>
                                )}
                                {item.link &&
                                    item.link.map((link, i2) => (
                                        <LinkWrapper key={i2}>
                                            <LinkComponent
                                                link={link}
                                                underline
                                                anchor={link.anchor}
                                            />
                                        </LinkWrapper>
                                    ))}
                                {item.image?.src && <Image {...item.image} />}
                            </SideNavContainer>
                        </ContentContainer>
                    </Fragment>
                );
            })}
        </Container>
    );
};

export default ContentElement;
